<template>
  <div class="container-fluid">
    <!-- Původní hlavička -->
    <div class="row justify-content-between">
      <div class="col-auto my-5">
        <h1>Seznam kalkulací</h1>
      </div>
      <div class="col-auto my-5">
        <button class="btn btn-outline-success btn-lg" @click="calculationNew">
          <i class="bi-plus-circle-dotted"></i>
          <strong>&nbsp;&nbsp;Nová kalkulace</strong>
        </button>
      </div>
    </div>

    <!-- Vyhledávání s Bootstrap -->
    <div class="row my-3">
      <div class="col-auto d-flex align-items-center">
        <div class="input-group">
          <span class="input-group-text">
            <i class="bi bi-search"></i>
          </span>
          <input
              type="text"
              class="form-control"
              id="search"
              name="search"
              v-model="searchText"
              placeholder="Vyhledat kalkulaci..."
          >
          <button
              v-if="searchText"
              class="btn btn-outline-secondary"
              @click="resetSearch()"
              title="Vymazat vyhledávání"
          >
            <i class="bi-x-lg"></i>
          </button>
        </div>
      </div>
      <div v-role="['admin', 'owner', 'developer']" class="col-auto ms-auto">
        <div class="form-group my-3">
          <div class="form-check form-switch">
            <label class="form-check-label" for="deleted_switch">Zobrazit smazané kalkulace</label>
            <input class="form-check-input" type="checkbox" role="switch" id="deleted_switch"
                   name="cutoff" v-model="deletedCalculations">
          </div>
        </div>
      </div>
    </div>

    <!-- Individuální filtry -->
    <CalculationListFilters :collapsed-default="true" v-model="filters"/>

    <!-- Tabulka s Bootstrap třídami -->
    <div class="table-responsive">
      <table class="table table-striped table-hover fixed-layout">
        <thead class="table-light">
        <tr>
          <th class="col-expand">
            <button
                class="btn btn-sm btn-outline-danger"
                @click="collapseAllVersions"
                :title="'Sbalit všechny verze'"
                :disabled="!hasExpandedRows"
                v-tooltip="'Sbalit všechny rozbalené verze'"
            >
              <i class="bi bi-chevron-double-up"></i>
            </button>
          </th>
          <th class="col-customer">Zákazník</th>
          <th class="col-name">Název</th>
          <th class="col-ikony">&nbsp;</th>
          <th class="col-number">Číslo</th>
          <th class="col-date">Datum vytvoření</th>
          <th class="col-status">Status</th>
          <th class="col-actions">Akce</th>
        </tr>
        </thead>

        <tbody>
        <template v-for="(item, key) in calculationList" :key="key">

          <!-- Hlavní řádek kalkulace -->
          <tr class="align-middle custom-row-height"
              :ref="el => setCalculationRowRef(item.uid, el)"
              :class="{
                'table-warning': item.confirmed,
                'table-info': isRowFocused(item.uid)
              }"
              style="cursor: pointer;">
            <td>
              <div class="d-flex justify-content-center">
                <button
                    class="btn btn-outline-secondary rounded-circle p-0"
                    style="width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; border-width: 1px;"
                    @click.stop="toggleVersions(item.uid)"
                    :title="isRowExpanded(item.uid) ? 'Sbalit verze' : 'Zobrazit verze'"
                    v-tooltip="'Zobrazit / skrýt verze'"
                >
                  <i class="bi" :class="isRowExpanded(item.uid) ? 'bi-chevron-up' : 'bi-chevron-down'"
                     style="font-size: 10px;"></i>
                </button>
              </div>
            </td>
            <td>
              <div class="d-flex flex-column line-height-sm">
                <span class="text-truncate fw-bold">
                  <HighlightText :text="customerPrimaryName(item.customer)" :query="searchText"/>
                </span>
                <span v-if="customerSecondaryName(item.customer)" class="text-muted text-truncate" style="font-size: 0.875rem;">
                  <HighlightText :text="customerSecondaryName(item.customer)" :query="searchText"/>
                </span>
              </div>
            </td>
            <td>
              <HighlightText :text="item.name" :query="searchText"/>
            </td>
            <td>
              <span v-if="item.internal_note && item.internal_note.length > 0" v-tooltip="item.internal_note"><i
                  class="bi bi-chat-left-dots-fill text-black-50"></i>&nbsp;</span>
              <span v-if="item.has_order" v-tooltip="'Vytvořená objednávka'"><i
                  class="bi bi-bag-fill text-black-50"></i>&nbsp;</span>
            </td>
            <td>
              <HighlightText :text="item.number" :query="searchText"/>
            </td>
            <td>{{ formatDate(item.created_at) }}</td>
            <td>
                <span class="badge" :class="getStatusBadgeClass(item.status)">
                  {{ item.status }}
                </span>
            </td>
            <td>
              <button
                  class="btn btn-outline-primary me-1"
                  :class="{ 'disabled': item.confirmed || item.deleted}"
                  @click.stop="calculationEdit(item.uid)"
                  title="Editovat kalkulaci"
                  v-tooltip=""
                  :disabled="item.confirmed"
              >
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button
                  class="btn btn-outline-secondary me-1"
                  @click.stop="calculationCopy(item.uid)"
                  title="Vytvořit kopii"
                  v-tooltip=""
              >
                <i class="bi bi-file-earmark-plus-fill"></i>
              </button>
              <button
                  v-if="!item.deleted"
                  class="btn btn-outline-danger me-1"
                  :class="{ 'disabled': item.confirmed }"
                  @click.stop="calculationDelete(item)"
                  title="Smazat kalkulaci"
                  v-tooltip=""
              >
                <i class="bi bi-trash-fill"></i>
              </button>
              <button
                  v-if="item.deleted"
                  class="btn btn-outline-success me-1"
                  :class="{ 'disabled': item.confirmed }"
                  @click.stop="calculationRestore(item)"
                  title="Obnovit kalkulaci"
                  v-tooltip=""
              >
                <i class="bi bi-arrow-counterclockwise"></i>
              </button>
              <button
                  v-role="['admin', 'owner', 'developer']"
                  class="btn btn-outline-secondary me-1"
                  @click.stop="toggleHistoryVisibility(item.uid)"
                  title="Zobrazit historii kalkulace"
                  v-tooltip=""
              >
                <i class="bi bi-clock-history"></i>
              </button>
            </td>
          </tr>

          <!-- Řádek s verzemi -->
          <CalculationVersionsRow
              v-if="isRowExpanded(item.uid)"
              :calculation="item"
              :ref="el => setVersionRowRef(item.uid, el)"
              @versionsLoaded="onVersionsLoaded"
              @calculationConfirmationChanged="onCalculationConfirmationChanged"
          />
        </template>
        </tbody>
      </table>
    </div>

    <!-- Prázdný stav s Bootstrap -->
    <div v-if="calculationList.length === 0" class="text-center py-5">
      <div class="mb-3">
        <i class="bi bi-calculator display-1 text-muted"></i>
      </div>
      <h3 class="text-muted">Žádné kalkulace</h3>
      <p class="text-muted">{{
          searchText ? 'Nebyli nalezeny žádné kalkulace odpovídající vašemu vyhledávání.' : 'Zatím nemáte vytvořené žádné kalkulace.'
        }}</p>
      <button v-if="!searchText" class="btn btn-primary btn-lg" @click="calculationNew">
        <i class="bi bi-plus-lg me-2"></i>
        Vytvořit první kalkulaci
      </button>
    </div>

    <!-- Univerzální komponenta pro stránkování -->
    <UniversalPagination
        v-if="calculationList.length > 0"
        :current-page="currentPage"
        :total-pages="pagination.total_pages || 1"
        :per-page="perPage"
        :per-page-options="perPageSelect"
        per-page-label="Položek na stránku"
        @page-changed="onPageChanged"
        @per-page-changed="onPerPageChanged"
    />

    <CalculationHistoryModal v-model:isVisible="isCalculationHistoryVisible" :calculationId="calculationHistoryRef"/>
  </div>
</template>

<script setup>
import {ref, watch, onMounted, computed, nextTick} from 'vue'
import {useRouter} from 'vue-router'
import {useStore} from 'vuex'
import CalculationVersionsRow from '@/components/versions/CalculationVersionsRow.vue'
import UniversalPagination from "@/components/base/UniversalPagination.vue";
import CalculationListFilters from "@/components/calculation/CalculationListFilters.vue";
import HighlightText from "@/components/base/HighlightText.vue";
import CalculationHistoryModal from "@/components/calculation/CalculationHistoryModal.vue";

const router = useRouter()
const store = useStore()

const isCalculationHistoryVisible = ref(false)
const calculationHistoryRef = ref(null)
const calculationList = ref([])
const pagination = ref({})
const perPageSelect = ref([10, 20, 50, 100, 500])
const isMaterialLoaded = ref(false)
const deletedCalculations = ref(false)
const filters = ref({
  hasOrder: false,
  hasInternalNote: false
})

// Reference na řádky tabulky pro scrollování
const versionRowRefs = ref(new Map())
const calculationRowRefs = ref(new Map())

// Computed properties pro store state
const currentPage = computed({
  get: () => store.getters['calculation_list/getCurrentPage'],
  set: (value) => store.dispatch('calculation_list/setCurrentPage', value)
})

const perPage = computed({
  get: () => store.getters['calculation_list/getPerPage'],
  set: (value) => store.dispatch('calculation_list/setPerPage', value)
})

const searchText = computed({
  get: () => store.getters['calculation_list/getSearchText'],
  set: (value) => store.dispatch('calculation_list/setSearchText', value)
})

// Computed property pro kontrolu, zda jsou nějaké rozbalené řádky
const hasExpandedRows = computed(() => {
  const expandedRows = store.getters['calculation_list/getExpandedRows']
  return expandedRows && expandedRows.size > 0
})

const toggleHistoryVisibility = (cid) => {
  isCalculationHistoryVisible.value = true;
  calculationHistoryRef.value = cid;

}

const customerPrimaryName = (customer) => {
  if (!customer) return 'Neznámý zákazník'
  if (customer.name && customer.name.trim() !== '') {
    return customer.name
  }
  const fullName = `${customer.last_name || ''} ${customer.first_name || ''}`.trim()
  return fullName || 'Neznámý zákazník'
}

const customerSecondaryName = (customer) => {
  if (!customer) return ''
  const fullName = `${customer.last_name || ''} ${customer.first_name || ''}`.trim()
  const email = customer.email || ''

  if (customer.name && customer.name.trim() !== '') {
    // Pokud je name vyplněno, zobrazíme fullName a email oddělený čárkou
    let parts = []
    if (fullName) parts.push(fullName)
    if (email) parts.push(email)
    return parts.join(', ')
  }

  // Pokud name není, zobrazíme pouze email (fullName už je v primárním jménu)
  return email
}

const isRowExpanded = (uid) => store.getters['calculation_list/isRowExpanded'](uid)
const isRowFocused = (uid) => store.getters['calculation_list/isRowFocused'](uid)

const setVersionRowRef = (uid, el) => {
  if (el) {
    versionRowRefs.value.set(uid, el)
  } else {
    versionRowRefs.value.delete(uid)
  }
}

const setCalculationRowRef = (uid, el) => {
  if (el) {
    calculationRowRefs.value.set(uid, el)
  } else {
    calculationRowRefs.value.delete(uid)
  }
}

// Funkce pro sbalení všech verzí
const collapseAllVersions = async () => {
  await store.dispatch('calculation_list/clearExpandedRows')
  await store.dispatch('calculation_list/clearFocusedRow')
}

const scrollToFocusedRow = async () => {
  const focusedUid = store.getters['calculation_list/getFocusedRowUid']
  if (focusedUid) {
    await nextTick()
    const rowRef = calculationRowRefs.value.get(focusedUid)
    if (rowRef) {
      rowRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }
}

const loadVersionsForExpandedRows = async () => {
  // Po vykreslení DOM načteme verze pro všechny rozbalené řádky
  await nextTick()

  const expandedRows = store.getters['calculation_list/getExpandedRows']
  const expandedUids = Array.from(expandedRows)

  for (const uid of expandedUids) {
    // Zkontrolujeme, jestli je daná kalkulace v aktuálně načtených datech
    const calculationExists = calculationList.value.some(calc => calc.uid === uid)

    if (calculationExists) {
      // Počkáme na vykreslení komponenty a pak načteme verze
      setTimeout(async () => {
        const versionRowRef = versionRowRefs.value.get(uid)
        if (versionRowRef && typeof versionRowRef.loadVersions === 'function') {
          await versionRowRef.loadVersions()
        }
      }, 150) // Delší zpoždění pro načítání ze store
    } else {
      // Pokud kalkulace není v aktuálních datech, odebereme ji z rozbalených
      store.dispatch('calculation_list/toggleExpandedRow', uid)
    }
  }
}

const toggleVersions = async (uid) => {
  const wasExpanded = isRowExpanded(uid)

  await store.dispatch('calculation_list/toggleExpandedRow', uid)

  if (!wasExpanded) {
    // Rozbalení - nastavit focus a načíst verze
    await store.dispatch('calculation_list/setFocusedRow', uid)

    setTimeout(async () => {
      const versionRowRef = versionRowRefs.value.get(uid)
      if (versionRowRef && typeof versionRowRef.loadVersions === 'function') {
        await versionRowRef.loadVersions()
      }
    }, 100) // Krátké zpoždění pro vykreslení komponenty
  } else {
    // Sbalení - pokud je focus na tomto řádku, zrušit ho
    const focusedUid = store.getters['calculation_list/getFocusedRowUid']
    if (focusedUid === uid) {
      await store.dispatch('calculation_list/clearFocusedRow')
    }
  }
}

const onVersionsLoaded = (versions) => {
  console.log('Verze byly načteny:', versions.length)
}

const listCalculation = async () => {
  isMaterialLoaded.value = false
  const result = await store.dispatch('calculation/listCalculation', {
    page: currentPage.value,
    per_page: perPage.value,
    search: searchText.value,
    deleted: deletedCalculations.value,
    has_order: filters.value.hasOrder,
    has_internal_note: filters.value.hasInternalNote
  })

  isMaterialLoaded.value = true
  pagination.value = result.pagination
  calculationList.value = result.data
  console.log(calculationList.value[0])

  // Vyčistit rozbalené řádky při vyhledávání (ale ne při změně stránky)
  if (searchText.value && currentPage.value === 1) {
    store.dispatch('calculation_list/clearExpandedRows')
  }

  // Načíst verze pro rozbalené řádky
  await loadVersionsForExpandedRows()

  // Scrollovat na focused řádek po načtení dat
  await scrollToFocusedRow()
}

const calculationEdit = (uid) => {
  // Nastavit focus při kliknutí na editaci
  store.dispatch('calculation_list/setFocusedRow', uid)

  router.push({
    name: 'calculationEdit',
    query: {cid: uid}
  })
}

const calculationCopy = (uid) => {
  store.dispatch('calculation/copyCalculation', uid)
      .then((result) => {
        if (result) {
          listCalculation()
          store.dispatch('calculation_list/setFocusedRow', result.uid)
        }
      })
}

const calculationDelete = (item) => {
  if (confirm(`Opravdu chcete smazat kalkulaci: ${item.number} - ${item.name}?`)) {
    store.dispatch('calculation/deleteCalculation', item.uid)
        .then((result) => {
          if (result.status) {
            if (store.getters['calculation_list/getFocusedRowUid'] === item.uid) {
              store.dispatch('calculation_list/clearFocusedRow')
            }
            listCalculation()
          }
        })
  }
}

const calculationRestore = (item) => {
  if (confirm(`Opravdu chcete obnovit kalkulaci: ${item.number} - ${item.name}?`)) {
    store.dispatch('calculation/restoreCalculation', item.uid)
        .then((result) => {
          if (result.status) {
            listCalculation()
          }
        })
  }
}

const calculationNew = () => {
  store.dispatch('calculation/createNewCalculation')
      .then((result) => {
        store.dispatch('calculation_list/setFocusedRow', result.uid)
        router.push({
          name: 'calculationEdit',
          query: {cid: result.uid}
        })
      })
}

// Handlers pro Pagination komponentu
const onPageChanged = (newPage) => {
  currentPage.value = newPage
}

const onPerPageChanged = (newPerPage) => {
  perPage.value = newPerPage
}

const resetSearch = () => {
  searchText.value = ""
}

const onCalculationConfirmationChanged = (calculationUid, isConfirmed) => {
  const calculationIndex = calculationList.value.findIndex(calc => calc.uid === calculationUid)

  if (calculationIndex !== -1) {
    calculationList.value[calculationIndex].confirmed = isConfirmed
    calculationList.value[calculationIndex].status = isConfirmed ? 'order_confirmed' : 'calculated'
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('cs-CZ')
}

const getStatusBadgeClass = (status) => {
  const statusMap = {
    'in_progress': 'text-bg-secondary',
    'calculated': 'text-bg-success',
    'edited': 'text-bg-primary',
    'new': 'text-bg-dark',
    'order_confirmed': 'text-bg-warning',
    'final_invoice': 'text-bg-danger'
  }
  return statusMap[status?.toLowerCase()] || 'bg-light text-dark'
}

watch(perPage, () => {
  listCalculation();
}, {deep: true})

watch(currentPage, () => {
  listCalculation();
}, {deep: true})

watch(searchText, () => {
  currentPage.value = 1;
  listCalculation();
}, {deep: true})

watch(deletedCalculations, () => {
  listCalculation();
})

watch(filters, () => {
  currentPage.value = 1;
  listCalculation();
}, {deep: true})

onMounted(() => {
  listCalculation();
})

</script>

<style scoped>

.fixed-layout {
  table-layout: fixed;
  width: 100%;
}

.col-expand {
  width: 56px;
}

.col-customer {
  width: 18%;
}

.col-ikony {
  width: 5%;
}

.col-name {
  width: 40%;
}

.col-number {
  width: 8%;
}

.col-date {
  width: 9%;
}

.col-status {
  width: 10%;
}

.col-actions {
  width: 15%;
}

.table td, .table th {
  overflow: hidden;
  text-overflow: clip;
  word-break: keep-all;
  white-space: normal;
  padding: 0.75rem;
}

/* Pro tooltip při dlouhém textu */
.table td[title]:hover {
  position: relative;
  overflow: visible;
}

/* Minimální vlastní stylování */
.table tbody tr {
  transition: background-color 0.15s ease-in-out;
}

.custom-row-height {
  height: 50px; /* Nebo jiná požadovaná výška */
}

.line-height-sm {
  line-height: 1.5rem;
}

.line-height-sm :deep(span) {
  font-size: inherit;
}
</style>